export const BOARD_COLUMNS_META = {
  people: {
    label: 'People',
    searchable: true,
    supported_options: ['enable_compact_mode'],
  },
  status: {
    label: 'Status',
    editable: true,
    sortable: true,
  },
  text: {
    label: 'Text',
    editable: true,
    searchable: true,
    sortable: true,
  },
  long_text: {
    label: 'Long Text',
    editable: true,
    searchable: true,
    sortable: true,
  },
  date: {
    label: 'Date',
    editable: true,
    sortable: true,
    supported_options: ['date_format', 'time_format'],
  },
  numbers: {
    label: 'Numbers',
    editable: true,
    sortable: true,
    supported_options: ['number_format'],
  },
  timeline: {
    label: 'Timeline',
  },
  tags: {
    label: 'Tags',
    supported_options: ['enable_compact_mode'],
  },
  connect_boards: {
    label: 'Connect Boards',
    supported_options: ['enable_compact_mode', 'disable_tag_splitting'],
  },
  mirror: {
    label: 'Mirror',
    supported_options: ['mirror_column_formatter', 'mirror_column_done_status', 'number_format', 'enable_compact_mode'],
  },
  rating: {
    label: 'Rating',
  },
  dropdown: {
    label: 'Dropdown',
    searchable: true,
    supported_options: ['enable_compact_mode'],
  },
  checkbox: {
    label: 'Checkbox',
  },
  link: {
    label: 'Link',
    searchable: true,
  },
  country: {
    label: 'Country',
    searchable: true,
  },
  location: {
    label: 'Location',
  },
  phone: {
    label: 'Phone',
    searchable: true,
  },
  item_id: {
    label: 'Item ID',
  },
  color_picker: {
    label: 'Color Picker',
  },
  time_tracking: {
    label: 'Time Tracking',
  },
  file: {
    label: 'File',
    editable: true,
    supported_options: ['enable_file_viewer'],
  },
  email: {
    label: 'Email',
    searchable: true,
  },
  hour: {
    label: 'Hour',
  },
  vote: {
    label: 'Vote',
  },
  board_relation: {
    label: 'Connect Boards',
    supported_options: ['enable_compact_mode', 'disable_tag_splitting'],
  },
  name: {
    label: 'Item',
    searchable: true,
    sortable: true,
  },
  formula: {
    label: 'Formula',
  },

  // new supported columns
  progress: {
    label: 'Progress',
  },
  week: {
    label: 'Week',
  },
  world_clock: {
    label: 'World Clock',
  },
  last_updated: {
    label: 'Last Updated',
  },

  // unsupported columns
  auto_number: {
    label: 'Auto Number',
    unsupported: true,
  },
  subtasks: {
    label: 'Subitems',
    ignore: true,
    unsupported: true,
  },
  doc: {
    label: 'monday Doc',
    unsupported: true,
  },
  creation_log: {
    label: 'Creation Log',
    ignore: true,
    unsupported: true,
  },
  button: {
    label: 'Button',
    unsupported: true,
  },
};

export function isColumnTypeSearchable(columnType: string | undefined) {
  if (!columnType) {
    return false;
  }

  const columnMeta = BOARD_COLUMNS_META[columnType as keyof typeof BOARD_COLUMNS_META];

  return !!columnMeta && 'searchable' in columnMeta && columnMeta.searchable === true;
}

// Whether clients may sort a column of this type. Only types flagged `sortable` in
// BOARD_COLUMNS_META qualify, so an unverified type renders as a plain header instead of a click
// that the monday API's items_page order_by rejects and that would fail the whole section.
export function isColumnTypeSortable(columnType: string | undefined) {
  if (!columnType) {
    return false;
  }

  const columnMeta = BOARD_COLUMNS_META[columnType as keyof typeof BOARD_COLUMNS_META];

  return !!columnMeta && 'sortable' in columnMeta && columnMeta.sortable === true;
}

export const DATE_FORMATS = [
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
];

export const TIME_FORMATS = [
  { value: '24h', label: '24h' },
  { value: '12h', label: '12h' },
];

export const NUMBER_FORMAT_LOCALES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'de-DE', label: 'German' },
  { value: 'fr-FR', label: 'French' },
  { value: 'es-ES', label: 'Spanish' },
  { value: 'it-IT', label: 'Italian' },
  { value: 'pt-PT', label: 'Portuguese (Portugal)' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)' },
  { value: 'ja-JP', label: 'Japanese' },
  { value: 'zh-CN', label: 'Chinese (Simplified)' },
  { value: 'he-IL', label: 'Hebrew' },
  { value: 'ko-KR', label: 'Korean' },
  { value: 'nl-NL', label: 'Dutch' },
  { value: 'sv-SE', label: 'Swedish' },
  { value: 'da-DK', label: 'Danish' },
  { value: 'nb-NO', label: 'Norwegian' },
  { value: 'fi-FI', label: 'Finnish' },
  { value: 'pl-PL', label: 'Polish' },
  { value: 'he-IL', label: 'Hebrew' },
  { value: 'ar-SA', label: 'Arabic' },
];

export const NUMBER_FORMAT_STYLES = [
  { value: 'decimal', label: 'Decimal' },
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
  { value: 'unit', label: 'Unit' },
];

export const MIRROR_COLUMN_FORMATTERS = [
  { value: 'progress', label: 'Progress' },
  { value: 'sum', label: 'Summarize linked items' },
];

export const ERROR_CODES = {
  // Mainly Portal Backend Errors
  INVALID_BOARD_ID: {
    message: 'Invalid board ID.',
  },
  INVALID_MONDAY_SESSION: {
    message: 'Invalid monday session.',
  },
  MISSING_INTEGRATION_USER: {
    message: 'No integration user defined.',
  },
  INVALID_INTEGRATION_USER: {
    message: 'Invalid integration user.',
  },
  INVALID_BOARD_OWNERS: {
    message: 'Invalid board owners.',
  },
  INTEGRATION_USER_NOT_BOARD_OWNER: {
    message: 'Integration user is not a board owner.',
  },
  USER_NOT_OWNER: {
    message: 'User is not a board owner.',
  },
  INVALID_ITEM_ID: {
    message: `Invalid item. Doesn't exist or doesn't belong to the board.`,
  },
  ACCOUNT_ALREADY_EXISTS: {
    message: 'Account already exists.',
  },
  OAUTH_AUTHORIZE_URL_FAILED: {
    message: 'Could not start the authorization. Please try again in a moment.',
  },
  MISSING_OAUTH_TOKEN: {
    message: `User didn't grant the app permissions.`,
  },
  OAUTH_EXCHANGE_FAILED: {
    message: 'monday.com rejected the authorization. Please try authorizing again.',
  },
  OAUTH_TOKEN_STORAGE_FAILED: {
    message: 'The authorization succeeded but could not be saved. Please try again in a moment.',
  },

  // Mainly Portal Client Errors
  MISSING_TOKEN: {
    message: 'Missing access token.',
  },
  INVALID_TOKEN: {
    message: 'Invalid access token.',
  },
  SESSION_EXPIRED: {
    message: 'Session expired, please sign in again.',
  },
  INVALID_SESSION: {
    message: 'Invalid session',
  },
  GET_PORTAL_SETTINGS_FROM_STORAGE_FAILED: {
    message: 'Failed to get portal settings from monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  SET_PORTAL_SETTINGS_IN_STORAGE_FAILED: {
    message: 'Failed to set portal settings in monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  BACKUP_PORTAL_SETTINGS_FAILED: {
    message: 'Failed to backup portal settings in monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  DELETE_PORTAL_SETTINGS_IN_STORAGE_FAILED: {
    message: 'Failed to delete portal settings in monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  GET_ACCESS_TOKEN_FAILED: {
    message:
      'Failed to get access token of integration user from monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  GET_ITEM_ID_BY_ACCOUNT_HASH_FROM_STORAGE_FAILED: {
    message: 'Failed to get item ID by account hash from monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  SET_ITEM_ID_BY_ACCOUNT_HASH_IN_STORAGE_FAILED: {
    message: 'Failed to set item ID by account hash in monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  DELETE_ITEM_ID_BY_ACCOUNT_HASH_IN_STORAGE_FAILED: {
    message: 'Failed to delete item ID by account hash from monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  GET_GOOGLE_ACCOUNT_ID_FROM_STORAGE_FAILED: {
    message: 'Failed to get Google account ID from monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  SET_GOOGLE_ACCOUNT_ID_IN_STORAGE_FAILED: {
    message: 'Failed to set Google account ID in monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  DELETE_GOOGLE_ACCOUNT_ID_FROM_STORAGE_FAILED: {
    message: 'Failed to delete Google account ID from monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  GET_CLIENT_SETTINGS_FROM_STORAGE_FAILED: {
    message: 'Failed to get client settings from monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  CLIENT_SETTINGS_MALFORMED: {
    message: 'Client settings are malformed.',
  },
  SEARCH_CLIENT_SETTINGS_IN_STORAGE_FAILED: {
    message: 'Failed to search client settings in monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  SEARCH_CLIENT_SETTINGS_NOT_SUCCESSFUL: {
    message: 'Searching client settings was not successful. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  SEARCH_PORTAL_STORAGE_FAILED: {
    message: 'Failed to search portal storage in monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  SEARCH_PORTAL_STORAGE_NOT_SUCCESSFUL: {
    message: 'Searching portal storage was not successful. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  DELETE_PORTAL_STORAGE_KEY_FAILED: {
    message: 'Failed to delete a portal storage entry from monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  SET_CLIENT_SETTINGS_IN_STORAGE_FAILED: {
    message: 'Failed to set client settings in monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  UPSERT_PORTAL_SETTINGS_IN_STORAGE_FAILED: {
    message: 'Failed to upsert portal settings in monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  INVALID_PORTAL_SETTINGS: {
    message: 'Invalid portal settings.',
  },
  GET_SUBSCRIPTIONS_FROM_STORAGE_FAILED: {
    message: 'Failed to get subscriptions from monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  GET_SUBSCRIPTIONS_FROM_STORAGE_BY_WEBHOOK_ID_FAILED: {
    message: 'Failed to get subscription from monday.com storage by webhook ID. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  DELETE_SUBSCRIPTION_IN_STORAGE_FAILED: {
    message: 'Failed to delete subscription from monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  SET_SUBSCRIPTION_IN_STORAGE_FAILED: {
    message: 'Failed to set subscription in monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  SET_SUBSCRIPTION_BY_WEBHOOK_ID_IN_STORAGE_FAILED: {
    message: 'Failed to set subscription by webhook ID in storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  SUBSCRIPTIONS_MALFORMED: {
    message: 'Subscriptions are malformed.',
  },
  TRIGGER_AUTOMATION_FAILED: {
    message: 'Failed to trigger automation.',
  },
  NO_INTEGRATION_USER: {
    message: 'No integration user found for this portal.',
  },
  GET_PORTAL_SETTINGS_FAILED: {
    message: 'Failed to get portal settings from monday.com storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true, // probably because of storage
  },
  NO_PORTAL_SETTINGS: {
    message: 'No portal settings found.',
  },
  GOOGLE_AUTH_DISABLED: {
    message: 'Google authentication is disabled for this portal.',
  },
  GOOGLE_AUTH_MISSING_CONFIGURATION: {
    message: 'Google authentication is missing configuration.',
  },
  GOOGLE_AUTH_INVALID_PAYLOAD: {
    message: 'Google authentication failed because of an invalid payload.',
  },
  GOOGLE_AUTH_INVALID_DOMAIN: {
    message: 'Google authentication failed because the email domain is not allowed.',
  },
  GOOGLE_AUTH_EMAIL_NOT_VERIFIED: {
    message: 'Google authentication failed because the email address is not verified.',
  },
  GOOGLE_AUTH_FAILED: {
    message: 'Google authentication failed.',
  },
  GOOGLE_ACCOUNT_ID_MISMATCH: {
    message: 'Google account ID mismatch.',
  },
  CLIENT_ITEM_SUMMARY_SECTION_NOT_FOUND: {
    message: 'Client item summary section not found in portal settings.',
  },
  KEY_FIGURES_NOT_FOUND: {
    message: 'Key figures section not found in portal settings, or none of its figures is computed over this section.',
  },
  SECTION_NOT_FOUND: {
    message: 'Section not found in portal settings.',
  },
  SECTION_DESCRIPTOR_NOT_FOUND: {
    message: 'Section descriptor not found in portal settings.',
  },
  INVALID_SECTION_DESCRIPTOR: {
    message: 'Invalid section descriptor.',
  },
  INVALID_BOARD_SOURCE: {
    message: 'Invalid board source.',
  },
  BOARD_SOURCE_NOT_FOUND: {
    message: 'Board source not found in portal settings.',
  },
  INVALID_BOARD_SOURCE_OPTIONS: {
    message: 'Invalid board source options.',
  },
  INVALID_ACTION: {
    message: 'Invalid action.',
  },
  FETCH_CLIENT_DATA_FAILED: {
    message: 'Failed to fetch client item or client settings.',
  },
  GET_CLIENT_ITEM_FAILED: {
    message: 'Failed to get client item.',
  },
  GET_CLIENT_SETTINGS_FAILED: {
    message: 'Failed to get client settings.',
  },
  NO_CLIENT_SETTINGS: {
    message: 'No client settings found for this client.',
  },
  ACCOUNT_NOT_AUTHORIZED: {
    message: 'Account not authorized to access this resource.',
  },
  MONDAY_API_INTERNAL_SERVER_ERROR: {
    message: 'The monday.com API is currently experiencing issues. This is most likely temporary, please try again later.',
    isLikelyTemporary: true,
  },
  MONDAY_API_TEMPORARILY_UNAVAILABLE: {
    message:
      'The monday.com API is temporarily unavailable. This can occur when monday.com is experiencing issues, please try again later.',
    isLikelyTemporary: true,
  },
  MONDAY_API_RESOURCE_ACCESS_FAILED: {
    message:
      'Failed to access resource: This can occur if the configured board or columns have been deleted (for example columns that were used in the filters) or if the integration user has lost access to them. Please ask your portal administrator to check the portal settings and resolve the issue.',
  },
  MONDAY_API_MISSING_PERMISSIONS: {
    message:
      'Missing permissions to access resource: This can occur if the integration user does not have the necessary permissions to access the underlying resource.',
  },
  MONDAY_API_UNAUTHORIZED: {
    message: 'Unauthorized: The integration user is not authorized to access the monday.com API.',
  },
  MONDAY_API_RATE_LIMITED: {
    message:
      'Rate limited by the monday.com API: This can occur if there are too many requests being made to the monday.com API in a short period of time. Please try again later.',
    isLikelyTemporary: true,
  },
  MONDAY_API_UNKNOWN_ERROR: {
    message:
      'An unknown error occurred while accessing the monday.com API. Chances are this is a temporary issue, please try again later. If the issue persists over an extended period, please contact support.',
    isLikelyTemporary: true,
  },
  CLIENTS_BOARD_FETCH_COLUMNS_FAILED: {
    message: 'Failed to fetch clients board columns.',
  },
  BOARD_SOURCE_PROCESSING_ERROR: {
    message: 'An error occurred while processing the board source.',
  },
  BOARD_NOT_FOUND: {
    message:
      'The board configured as the source for this section could not be found. This can occur if the board was deleted or if the integration user lost access to it. Please ask your portal administrator to check the portal settings and resolve the issue.',
  },
  CLIENT_COLUMN_NOT_FOUND: {
    message:
      'The client column configured in the board source settings could not be found on the board. This can occur if the column was deleted or if the integration user lost access to it. Please ask your portal administrator to check the portal settings and resolve the issue.',
    hideStatusLink: true,
  },
  MISSING_DEDICATED_BOARD: {
    message: 'There is no board configured for this client.',
    hideStatusLink: true,
  },
  FAILED_TO_LOAD_SUBITEMS: {
    message: 'Failed to load subitems.',
  },
  CLIENT_ITEM_NOT_FOUND: {
    message: 'The item associated with this client could not be found.',
  },
  CLIENT_ITEM_INVALID: {
    message: 'The item associated with this client is invalid.',
  },
  COLUMN_NOT_EDITABLE: {
    message: 'The column you are trying to edit is not editable.',
  },
  COLUMN_NOT_FOUND: {
    message: 'The column could not be found.',
  },
  COLUMN_NOT_OF_TYPE_FILE: {
    message: 'The column configured for file uploads is not of type "File".',
  },
  VALUE_IS_NOT_A_STRING: {
    message: 'The value provided is not a string.',
  },
  VALUE_IS_NOT_A_VALID_DATE_FORMAT: {
    message: 'Invalid date format. Expected YYYY-MM-DD.',
  },
  VALUE_IS_NOT_A_VALID_TIME_FORMAT: {
    message: 'Invalid time format. Expected HH:MM:SS.',
  },
  VALUE_IS_NOT_A_VALID_DATE_OR_TIME_FORMAT: {
    message: 'Invalid date or time format for date column.',
  },
  MISSING_FILE_UPLOAD_CONFIGURATION: {
    message: 'File upload configuration is missing.',
  },
  FILE_UPLOAD_FAILED: {
    message: 'Failed to upload file to file column. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  CLEARING_FILE_COLUMN_FAILED: {
    message: 'Failed to clear file column. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  ACTION_ID_NOT_PERMITTED: {
    message: 'The action you are trying to perform is not permitted.',
  },
  TRIGGER_ACTION_FAILED: {
    message: 'Failed to trigger action.',
  },
  ITEM_COMMENTS_NOT_ALLOWED: {
    message: 'Comments on this item are not allowed.',
  },
  COMMENT_ID_INVALID: {
    message: 'Invalid comment ID.',
  },
  COMMENT_NOT_FOUND: {
    message: 'Comment not found.',
  },
  COMMENT_NOT_FROM_CLIENT: {
    message: 'The comment you are trying to edit/delete was not created by this client.',
  },
  ATTACHING_FILE_TO_COMMENT_FAILED: {
    message: 'Failed to attach file to comment. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  DELETING_COMMENT_FAILED: {
    message: 'Failed to delete comment. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  GET_ACCOUNT_HASH_FAILED: {
    message: 'Failed to get account hash.',
  },
  CLIENT_NOT_FOUND: {
    message: 'This client could not be found.',
  },
  CLIENT_DISABLED: {
    message: 'This client is currently disabled.',
  },
  ACCOUNT_NOT_FOUND: {
    message: 'This account could not be found.',
  },
  ACCOUNT_DISABLED: {
    message: 'Your account is currently disabled.',
  },
  PASSWORD_INCORRECT: {
    message: 'Your password is incorrect, please try again.',
  },
  PASSWORD_UPDATE_NOW_ALLOWED: {
    message: 'Password update is not allowed in this portal.',
  },
  PASSWORD_UPDATE_WRONG_ACCOUNT_TYPE: {
    message: 'Password update is only allowed for accounts of type "credentials".',
  },
  CURRENT_PASSWORD_INCORRECT: {
    message: 'Current password is incorrect.',
  },
  FILE_SIZE_EXCEEDS_LIMIT: {
    message: 'File size exceeds the 450 MB limit.',
  },
  FETCH_FILE_FROM_STORAGE_FAILED: {
    message: 'Failed to fetch file from storage. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
  FETCH_PORTAL_INFO_FAILED: {
    message: 'Failed to fetch portal info.',
  },
  ITEM_TOKEN_EXPIRED: {
    message: `Your access to this item has expired due to inactivity. Please refresh the page or section and try again.`,
  },
  INVALID_ITEM_TOKEN: {
    message: 'Invalid item token.',
  },
  MISSING_ITEM_TOKEN: {
    message: 'Missing item token.',
  },
  UNKNOWN_ERROR: {
    message: 'An unknown error occurred. This might be a temporary issue, please try again later.',
  },
  UNKNOWN_PROXY_ERROR: {
    message: 'An error occured on the Cloudflare proxy. This is likely a temporary issue, please try again later.',
    isLikelyTemporary: true,
  },
};
